import React, { useState, useEffect } from "react";
import { PRODUCT_CATEGORIES } from "../constants/categories";
import { hasSubcategories, subcategoriesFor } from "../constants/subcategories";
import { productsAPI, variantGroupsAPI } from "../services/api";
import { useI18n } from "../i18n";
import useShopCurrency from "../hooks/useShopCurrency";
import { useMembership } from "../context/MembershipContext";
import { formatMoney } from "../pos/utils/money";
import ImagePicker from "./ImagePicker";
import ConvertToRangeDialog from "./ConvertToRangeDialog";
import JuiceDetailsFields, {
  DEFAULT_JUICE_DETAILS,
  parseJuiceDetails,
  serializeJuiceDetails,
} from "./JuiceDetailsFields";

const inputClass =
  "w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500";
const labelClass = "block text-sm font-medium text-gray-700 mb-2";

const ProductForm = ({ product, onClose, onConverted }) => {
  const { t } = useI18n();
  const { can, moduleOn, flag } = useMembership();
  const dosesOn = flag("dose_sales");
  // Buy prices feed profit tracking: a shop that does not use it is not asked for them
  const seesCosts = can("power.see_costs") && moduleOn("finance");
  const currency = useShopCurrency();
  const [formData, setFormData] = useState({
    name: "",
    category: "juice",
    brand: "",
    subcategory: "",
    price: "",
    cost_price: "",
    discount_percentage: "0",
    stock_quantity: "",
    description: "",
    description_ar: "",
    description_fr: "",
    is_new: false,
    is_house_recipe: false,
    sold_by_dose: false,
    dose_price: "",
  });
  const [juiceDetails, setJuiceDetails] = useState(DEFAULT_JUICE_DETAILS);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  // The ranges this shop has; empty for a cashier, who cannot list them
  const [groups, setGroups] = useState([]);
  const [variantGroupId, setVariantGroupId] = useState("");
  const [variantLabel, setVariantLabel] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [converting, setConverting] = useState(false);
  // Archiving the generic product needs the delete right as well as the edit one
  const canConvert = Boolean(product) && can("products.edit") && can("products.delete");

  /**
   * What this product actually earns, shown while the owner types.
   *
   * Uses the discounted price rather than the shelf price, because that is
   * what a customer pays — a healthy-looking margin that ignores a standing
   * 20% discount is the kind of number that loses money quietly.
   */
  const margin = (() => {
    const price = parseFloat(formData.price);
    const cost = parseFloat(formData.cost_price);

    if (!Number.isFinite(price) || !Number.isFinite(cost) || price <= 0) {
      return null;
    }

    const discount = parseFloat(formData.discount_percentage) || 0;
    const sells = price * (1 - discount / 100);
    const profit = sells - cost;

    return { profit, ratio: sells > 0 ? profit / sells : 0 };
  })();

  useEffect(() => {
    variantGroupsAPI
      .list()
      .then(({ data }) => setGroups(data))
      .catch(() => setGroups([]));
  }, []);

  useEffect(() => {
    if (product) {
      setVariantGroupId(product.variant_group_id || "");
      setVariantLabel(product.variant_label || "");

      setFormData({
        name: product.name || "",
        category: product.category || "juice",
        brand: product.brand || "",
        subcategory: product.subcategory || "",
        price: product.price || "",
        cost_price: product.cost_price ?? "",
        discount_percentage: product.discount_percentage || "0",
        stock_quantity: product.stock_quantity || "",
        description: product.description || "",
        description_ar: product.description_ar || "",
        description_fr: product.description_fr || "",
        is_new: product.is_new || false,
        is_house_recipe: product.is_house_recipe || false,
        sold_by_dose: Boolean(product.sold_by_dose),
        dose_price: product.dose_price ?? "",
      });

      if (product.juice_details) {
        setJuiceDetails(parseJuiceDetails(product.juice_details));
      }

      // Load existing images
      if (product.images && Array.isArray(product.images)) {
        setExistingImages(product.images);
      }
    }
  }, [product]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const formDataToSend = new FormData();

      formDataToSend.append("name", formData.name);
      formDataToSend.append("category", formData.category);
      formDataToSend.append("brand", formData.brand || "");
      formDataToSend.append("subcategory", formData.subcategory || "");
      formDataToSend.append("price", parseFloat(formData.price));

      // Left empty means "not known yet" rather than free, so send nothing
      // instead of a zero that would show a 100% margin.
      if (formData.cost_price !== "" && formData.cost_price !== null) {
        formDataToSend.append("cost_price", parseFloat(formData.cost_price));
      }
      formDataToSend.append(
        "discount_percentage",
        parseFloat(formData.discount_percentage) || 0,
      );
      formDataToSend.append(
        "stock_quantity",
        parseInt(formData.stock_quantity),
      );
      formDataToSend.append("description", formData.description || "");
      formDataToSend.append("description_ar", formData.description_ar || "");
      formDataToSend.append("description_fr", formData.description_fr || "");
      formDataToSend.append("is_new", formData.is_new);
      formDataToSend.append("is_house_recipe", formData.is_house_recipe);
      // Only where the shop has dose selling; elsewhere the product keeps what it had
      if (dosesOn) {
        formDataToSend.append("sold_by_dose", formData.sold_by_dose);
        formDataToSend.append("dose_price", formData.dose_price === "" ? "" : parseFloat(formData.dose_price));
      }

      if (formData.category === "juice") {
        formDataToSend.append(
          "juice_details",
          JSON.stringify(serializeJuiceDetails(juiceDetails)),
        );
      }

      // "" is how a form says "no range", which also lets an owner take a
      // product out of one. Cashiers never see the picker, so send nothing.
      if (groups.length > 0 || variantGroupId) {
        formDataToSend.append("variant_group_id", variantGroupId);
        formDataToSend.append("variant_label", variantLabel);
      }

      // Add new image files
      selectedFiles.forEach((file) => {
        formDataToSend.append("images", file);
      });

      // Handle existing images for update
      if (product && existingImages.length > 0) {
        formDataToSend.append("existingImages", JSON.stringify(existingImages));
      }

      if (product) {
        await productsAPI.update(product.id, formDataToSend);
      } else {
        await productsAPI.create(formDataToSend);
      }

      onClose();
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          t("products.form.saveFailed"),
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      // Products.js renders this inline as the last child of a `space-y-6`
      // section — Tailwind's `space-y-*` puts a margin-top on every child
      // after the first, fixed positioning or not, which pushed this whole
      // overlay down from the viewport's actual top. An inline style is the
      // one thing guaranteed to outrank that rule regardless of which page
      // ends up mounting this form.
      style={{ marginTop: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-50 p-0 sm:p-4"
    >
      <div className="bg-white sm:rounded-xl shadow-2xl max-w-4xl w-full max-h-[100vh] sm:max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-800">
              {product ? t("products.form.editTitle") : t("products.form.addTitle")}
            </h2>
            <button
              onClick={onClose}
              aria-label={t("common.close")}
              className="text-gray-500 hover:text-gray-700 text-2xl"
            >
              ✕
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-600">{error}</p>
            </div>
          )}

          {product?.is_draft && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
              {t("products.form.draftBanner")}
            </div>
          )}

          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>{t("products.form.name")}</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                className={inputClass}
                required
              />
            </div>

            <div>
              <label className={labelClass}>{t("products.form.brand")}</label>
              <input
                type="text"
                value={formData.brand}
                onChange={(e) =>
                  setFormData({ ...formData, brand: e.target.value })
                }
                className={inputClass}
              />
            </div>

            <div>
              <label className={labelClass}>{t("products.form.category")}</label>
              <select
                value={formData.category}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    category: e.target.value,
                    subcategory: "",
                  })
                }
                className={inputClass}
                required
              >
                {PRODUCT_CATEGORIES.map((category) => (
                  <option key={category.value} value={category.value}>
                    {category.label}
                  </option>
                ))}
              </select>
            </div>

            {hasSubcategories(formData.category) && (
              <div>
                <label className={labelClass}>
                  {t("products.form.subcategory")}
                </label>
                <select
                  value={formData.subcategory}
                  onChange={(e) =>
                    setFormData({ ...formData, subcategory: e.target.value })
                  }
                  className={inputClass}
                >
                  <option value="">{t("products.form.noSubcategory")}</option>
                  {subcategoriesFor(formData.category).map((subcategory) => (
                    <option key={subcategory.value} value={subcategory.value}>
                      {subcategory.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label className={labelClass}>
                {t("products.form.price", { currency })}
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.price}
                onChange={(e) =>
                  setFormData({ ...formData, price: e.target.value })
                }
                className={inputClass}
                required
              />
            </div>

            {/* Only for someone given buy prices — the API drops it otherwise */}
            {seesCosts && (
              <div>
                <label className={labelClass}>
                  {t("products.form.buyPrice", { currency })}
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder={t("products.form.buyPricePlaceholder")}
                  value={formData.cost_price}
                  onChange={(e) =>
                    setFormData({ ...formData, cost_price: e.target.value })
                  }
                  className={inputClass}
                />
                {margin ? (
                  <p
                    className={`text-sm mt-1 ${
                      margin.profit < 0 ? "text-red-600" : "text-gray-600"
                    }`}
                  >
                    {t("products.form.youMake", {
                      amount: formatMoney(margin.profit, currency),
                      margin: (margin.ratio * 100).toFixed(0),
                    })}
                    {margin.profit < 0 && t("products.form.loss")}
                  </p>
                ) : (
                  <p className="text-xs text-gray-500 mt-1">
                    {t("products.form.costPrivate")}
                  </p>
                )}
              </div>
            )}

            <div>
              <label className={labelClass}>{t("products.form.discount")}</label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={formData.discount_percentage}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    discount_percentage: e.target.value,
                  })
                }
                className={inputClass}
              />
              {formData.price && formData.discount_percentage > 0 && (
                <p className="text-sm text-gray-600 mt-1">
                  {t("products.form.finalPrice", {
                    amount: formatMoney(
                      parseFloat(formData.price) *
                        (1 - parseFloat(formData.discount_percentage) / 100),
                      currency,
                    ),
                  })}
                </p>
              )}
            </div>

            <div>
              <label className={labelClass}>{t("products.form.stock")}</label>
              <input
                type="number"
                value={formData.stock_quantity}
                onChange={(e) =>
                  setFormData({ ...formData, stock_quantity: e.target.value })
                }
                className={inputClass}
                required
              />
            </div>

            <div className="flex items-center">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_new}
                  onChange={(e) =>
                    setFormData({ ...formData, is_new: e.target.checked })
                  }
                  className="w-5 h-5 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <span className="ms-2 text-sm font-medium text-gray-700">
                  {t("products.form.markNew")}
                </span>
              </label>
            </div>

            {/* A liquid poured into the customer's pod from an open bottle (the till's 💧) */}
            {dosesOn && formData.category === "juice" && (
              <div className="sm:col-span-2 p-3 rounded-lg border border-gray-200 bg-gray-50">
                <label className="flex items-start cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.sold_by_dose}
                    onChange={(e) => setFormData({ ...formData, sold_by_dose: e.target.checked })}
                    className="w-5 h-5 mt-0.5 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <span className="ms-3">
                    <span className="block text-sm font-medium text-gray-700">{t("doses.product.toggle")}</span>
                    <span className="block text-xs text-gray-500 mt-0.5">{t("doses.product.hint")}</span>
                  </span>
                </label>
                {formData.sold_by_dose && (
                  <div className="mt-3 max-w-xs">
                    <label className={labelClass}>{t("doses.product.price")}</label>
                    <input
                      type="number"
                      inputMode="decimal"
                      min="0"
                      step="0.001"
                      value={formData.dose_price}
                      onChange={(e) => setFormData({ ...formData, dose_price: e.target.value })}
                      className={inputClass}
                      required
                    />
                  </div>
                )}
              </div>
            )}

            {/*
              Your own mixes stay yours. Tick this and the product is excluded
              from the shared catalogue other shops can copy from.
            */}
            <div className="sm:col-span-2">
              <label className="flex items-start cursor-pointer p-3 rounded-lg border border-gray-200 bg-gray-50">
                <input
                  type="checkbox"
                  checked={formData.is_house_recipe}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      is_house_recipe: e.target.checked,
                    })
                  }
                  className="w-5 h-5 mt-0.5 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <span className="ms-3">
                  <span className="block text-sm font-medium text-gray-700">
                    {t("products.form.ownRecipe")}
                  </span>
                  <span className="block text-xs text-gray-500 mt-0.5">
                    {t("products.form.ownRecipeHint")}
                  </span>
                </span>
              </label>
            </div>
          </div>

          {/* Range: which variant group this product belongs to, if any */}
          {(groups.length > 0 || variantGroupId || canConvert) && (
            <div className="border border-gray-200 rounded-lg bg-gray-50 p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>{t("products.form.range")}</label>
                <select
                  value={variantGroupId}
                  onChange={(e) => setVariantGroupId(e.target.value)}
                  className={inputClass}
                >
                  <option value="">{t("products.form.noRange")}</option>
                  {groups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {t("products.form.rangeHint")}
                </p>
              </div>

              {variantGroupId && (
                <div>
                  <label className={labelClass}>
                    {t("products.form.variantLabel")}
                  </label>
                  <input
                    type="text"
                    value={variantLabel}
                    onChange={(e) => setVariantLabel(e.target.value)}
                    maxLength={100}
                    required
                    placeholder={t("products.form.variantLabelPlaceholder")}
                    className={inputClass}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {t("products.form.variantLabelHint")}
                  </p>
                </div>
              )}

              {/* A generic product becomes a range of specific ones. Rarely
                  needed, so it lives here rather than on the product's card;
                  a variant is already in a range, so it is shown but off. */}
              {canConvert && (
                <div className="md:col-span-2 border-t border-gray-200 pt-4">
                  <button
                    type="button"
                    onClick={() => setConverting(true)}
                    disabled={Boolean(product.variant_group_id)}
                    title={
                      product.variant_group_id
                        ? t("products.convert.disabledHint")
                        : undefined
                    }
                    className="px-4 py-2 rounded-lg bg-primary-50 hover:bg-primary-100 text-primary-700 font-medium disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-primary-50"
                  >
                    {t("products.convert.button")}
                  </button>
                  <p className="text-xs text-gray-500 mt-1">
                    {product.variant_group_id
                      ? t("products.convert.disabledHint")
                      : t("products.convert.formHint")}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Description */}
          <div>
            <label className={labelClass}>{t("products.form.description")}</label>
            <textarea
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              rows="3"
              className={inputClass}
            />
          </div>

          <ImagePicker
            existing={existingImages}
            onExistingChange={setExistingImages}
            onFilesChange={setSelectedFiles}
            editing={Boolean(product)}
          />

          {formData.category === "juice" && (
            <JuiceDetailsFields value={juiceDetails} onChange={setJuiceDetails} />
          )}

          {/* Actions */}
          <div className="flex justify-end gap-4 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium transition-all"
            >
              {t("common.cancel")}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-semibold transition-all disabled:opacity-50"
            >
              {loading
                ? t("products.form.saving")
                : product
                  ? t("products.form.update")
                  : t("products.form.create")}
            </button>
          </div>
        </form>
      </div>

      {/* Outside the form: it has a form of its own, and a form inside a form
          would submit this one */}
      {converting && (
        <ConvertToRangeDialog
          product={product}
          onClose={() => setConverting(false)}
          onConverted={(result, count) => {
            setConverting(false);
            onConverted?.(result, count);
          }}
        />
      )}
    </div>
  );
};

export default ProductForm;
